/*
 * Copyright (c) CIRROM 2021.
 */

import Container from "@mui/material/Container";
import Modal from "@mui/material/Modal";
import CardHeader from "@mui/material/CardHeader";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";

import React from "react";
import Typography from "@mui/material/Typography";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { amber } from "@mui/material/colors";

/**
 * ChickenOutModal Component
 *
 * A reusable modal dialog for displaying a confirmation message with a title and message.
 * This component is used to prompt the user for confirmation before proceeding with an action
 * that they might want to reconsider.
 *
 * @param {boolean} props.modalOpen - A boolean representing whether the modal is open or not.
 * @param {string} props.title - The title to display in the modal header.
 * @param {string} props.message - The message to display in the modal content.
 * @param {Function} props.onModalOpenChange - A function to handle changes in the modal's open state.
 *                                           Accepts a boolean parameter representing the new open state.
 * @param {Function} props.onSuccess - A function to be executed when the user confirms the action.
 *                                    Accepts an optional event parameter (typically used in onClick events).
 * @param {string} [props.confirmLabel] - Names the action on the confirm button. Defaults to "Yes",
 *                                        which is all a caller that has not been given one can say.
 *                                        Consequential callers should pass the action itself, so the
 *                                        button reads as what it does rather than as an answer to a
 *                                        question the user has to re-read the title to recall.
 * @returns {JSX.Element} - The rendered ChickenOutModal component.
 */
export const ChickenOutModal = (props: {
  modalOpen: boolean;
  title: string;
  message: string;
  onModalOpenChange(change: boolean): void;
  onSuccess(event?): void;
  confirmLabel?: string;
}) => {
  // Destructure props for easier access
  const {
    modalOpen,
    onModalOpenChange,
    title,
    message,
    onSuccess,
    confirmLabel,
  } = props;

  return (
    // Modal dialog to display the confirmation message
    <Modal
      open={modalOpen}
      // Escape and a backdrop click resolve to Cancel. Dismissing a confirmation is
      // the safe outcome, so there is no reason to trap a keyboard user inside it.
      onClose={() => onModalOpenChange(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth={"sm"}>
        <Card>
          <CardHeader
            title={title}
            titleTypographyProps={{ variant: "h4" }}
            avatar={
              <WarningAmberOutlinedIcon
                style={{ width: "50px", height: "50px", color: amber[800] }}
              />
            }
          />
          <Divider />
          <CardContent>
            <Typography variant={"body1"}>{message}</Typography>
          </CardContent>
          <CardContent style={{ display: "flex", justifyContent: "flex-end" }}>
            {/* "Yes" button to confirm the action */}
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ margin: "2px", padding: "0em 0.5em" }}
              onClick={(event) => {
                onSuccess(event); // Execute the onSuccess function, passing the event if available
                onModalOpenChange(false); // Close the modal by setting the state to false
              }}
            >
              {confirmLabel ?? "Yes"}
            </Button>
            {/* "Cancel" button to reject the action */}
            <Button
              variant="text"
              color="inherit"
              size="small"
              style={{ margin: "2px", padding: "0em 0.5em" }}
              onClick={() => onModalOpenChange(false)} // Close the modal by setting the state to false
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Modal>
  );
};
